import { ErrorMessage, Form, Formik } from 'formik'
import { FC, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import SVG from 'react-inlinesvg'
import { createIntroduction, getCostIntroduction, getIntroductionTypes } from '../../services/introductionService'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../setup'
import { UserModel } from '../../modules/auth/models/UserModel'

interface RouteParams {
    id: string
}

interface IntroductionOption {
    id: number
    title?: string
    name?: string
    label?: string
}

interface RequestIntroductionForm {
    introduction_type: number
    topic: string
    message: string
    agree: boolean
}

const initialValues: RequestIntroductionForm = {
    introduction_type: 0,
    topic: '',
    message: '',
    agree: false,
}

export type IntroductionCost = {
    isFree: boolean;
    price: number;
    priceToReceiver: number;
    priceToNobilis: number;
    currency: string;
    reason: string;
    remainingFree: number;
};

const validationSchema = Yup.object({
    introduction_type: Yup.number()
        .moreThan(0, 'Type of introduction is required')
        .required('Type of introduction is required'),
    topic: Yup.string().max(150, 'Topic must be 150 characters or less').required('Topic is required'),
    message: Yup.string()
        .max(2000, 'Message must be 2000 characters or less')
        .required('Message is required'),
    agree: Yup.boolean()
        .oneOf([true], 'You must agree to the code of conduct and privacy guidelines')
        .required('You must agree to the code of conduct and privacy guidelines'),
})

const RequestIntroductionPage: FC = () => {
    const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const history = useHistory()
    const { id } = useParams<RouteParams>()
    const receiver = Number(id)
    const [introductionTypes, setIntroductionTypes] = useState<IntroductionOption[]>([])
    const [loadingTypes, setLoadingTypes] = useState(false)
    const [cost,setCost] = useState<IntroductionCost>()

    useEffect(() => {
        if (!/^\d+$/.test(id)) {
            history.goBack()
        }
    }, [history, id])

    useEffect(() => {
        let isMounted = true

        const fetchIntroductionTypes = async () => {
            try {
                setLoadingTypes(true)
                let cost_ = await getCostIntroduction({ receiver_id: receiver })
                if(!cost_){
                    history.goBack();
                    return;
                }
                let introductionTypes_ = await getIntroductionTypes()
                if (isMounted){
                    setCost(cost_)
                    setIntroductionTypes(Array.isArray(introductionTypes_) ? introductionTypes_ : [])
                }
            } finally {
                if (isMounted) setLoadingTypes(false)
            }
        }

        fetchIntroductionTypes()

        return () => {
            isMounted = false
        }
    }, [])

    const getIntroductionLabel = (item: IntroductionOption) => {
        return item.title || item.name || item.label || `Introduction ${item.id}`
    }

    const handleSubmit = async (values: RequestIntroductionForm) => {
        const payload = {
            receiver,
            topic: values.topic,
            message: values.message,
            status: 1,
            introduction_type: Number(values.introduction_type),
        }
        let success = await createIntroduction(payload)
        console.log('success',success)
        if(success?.id>0){
            history.push('/introduction/success')
        }
    }

    const textareaRef = useRef(null);
    const handleTextAreaInput = (e) => {
        const el = textareaRef.current;
        el.style.height = "auto"; // resetea para medir bien
        const lineHeight = 20;    // ajusta según tu font-size
        const maxHeight = 5 * lineHeight;

        el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount={true}
            onSubmit={handleSubmit}
        >
            {(formik) => (
                <Form className='introduction-member-intro'>
                    <div className='introduction-member-intro__left'>
                        <div className="help-center-sections w-100 tap-flex-center justify-content-start mb-5">
                            <div className="nb-link-underline-500 w-auto cursor-pointer" onClick={()=> history.goBack()}>
                            Back
                            </div>
                        </div>
                        <div className='introduction-member-intro__header'>
                            <span className='introduction-member-intro__tag w-100'>member introduction</span>
                            <h1 className='introduction-member-intro__title w-100'>Contact member</h1>
                            <p className='introduction-member-intro__description'>
                                To ensure member security, your request will undergo Nobilis' vetting process. If
                                approved, it will be forwarded to the member. Should the introduction be declined or
                                remain unanswered, your payment will be refunded, less the platform fee.
                            </p>
                        </div>

                        <div className='introduction-member-intro__form'>
                            <div className='introduction-form-group'>
                                <label htmlFor='introduction_type'>Type of introduction</label>
                                <div className='tap-add-experience-2-input-line'>
                                    <select className='tap-select-add-experience w-100 pl_0'
                                        id='introduction_type'
                                        name='introduction_type'
                                        value={formik.values.introduction_type}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={loadingTypes}
                                    >
                                        <option value={0} disabled>
                                            {loadingTypes
                                                ? 'Loading introduction types...'
                                                : 'Select the type of introduction'}
                                        </option>
                                        {introductionTypes.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {getIntroductionLabel(item)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <ErrorMessage
                                    name='introduction_type'
                                    component='div'
                                    className='tap-add-experience-text text-danger'
                                />
                            </div>

                            <div className='introduction-form-group'>
                                <label htmlFor='topic'>Topic</label>
                                <div className='tap-add-experience-2-input-line'>
                                    <input className='tap-add-experience-2-input'
                                        id='topic'
                                        name='topic'
                                        type='text'
                                        maxLength={150}
                                        value={formik.values.topic}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder='e.g. "Family office strategy", "Dialogue on investment & innovation"'
                                    />
                                </div>
                                <ErrorMessage
                                    name='topic'
                                    component='div'
                                    className='tap-add-experience-text text-danger'
                                />
                            </div>

                            <div className='introduction-form-group'>
                                <label htmlFor='message'>Message</label>
                                <div className='tap-add-experience-2-input-line h-auto'>
                                    <textarea ref={textareaRef} onInput={handleTextAreaInput}
                                        className='tap-add-experience-2-input textarea-autosize'
                                        id='message'
                                        name='message'
                                        maxLength={2000}
                                        value={formik.values.message}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder='Please write a message to the member explaining why you wish to connect and your preferred timing.'
                                    />
                                </div>
                                <ErrorMessage
                                    name='message'
                                    component='div'
                                    className='tap-add-experience-text text-danger'
                                />
                            </div>
                        </div>
                    </div>

                    <div className='introduction-member-intro__summary'>
                        <div className={`introduction-summary ${!cost?.isFree ? '' : 'd-none'}`}>
                            <div className='introduction-summary__row'>
                                <span>Introduction fee</span>
                                <span>{cost?.priceToReceiver}</span>
                            </div>
                            <div className='introduction-summary__row'>
                                <span>Platform fee</span>
                                <span>{cost?.priceToNobilis}</span>
                            </div>
                            <div className='introduction-summary__total'>
                                <span className='first-child'>Total Fee</span>
                                <span className='second-child'>{cost?.price}</span>
                            </div>
                        </div>
                        <div className={`introduction-summary ${cost?.isFree ? '' : 'd-none'}`}>
                            <div className='introduction-summary__total'>
                                <span className='first-child'>{cost?.remainingFree} complimentary introductions left</span>
                            </div>
                        </div>

                        <p className='introduction-summary__note'>
                            If your introduction request is declined by the recipient, the request fee will be refunded to your original payment method within 5–7 business days.
                        </p>

                        <div className='introduction-summary__checkbox'>
                            <div className='pf-checkbox mb-2'>
                                <input
                                    type='checkbox'
                                    id='i-agree-contact'
                                    name='agree'
                                    className='pf-check'
                                    checked={formik.values.agree}
                                    onChange={(event) => formik.setFieldValue('agree', event.target.checked)}
                                    onBlur={formik.handleBlur}
                                />
                                <label htmlFor='i-agree-contact' className='pf-checkbox-content tap-flex-center'>
                                    <div className='assignment-description mt-1'>
                                        I agree to adhere to the platform's code of conduct and privacy guidelines.
                                    </div>
                                </label>
                            </div>
                        </div>
                        <ErrorMessage name='agree' component='div' className='tap-add-experience-text text-danger' />

                        <button
                            type='submit'
                            className='introduction-btn-submit'
                            disabled={!formik.isValid || formik.isSubmitting || loadingTypes}
                        >
                            {formik.isSubmitting ? 'submitting...' : 'submit and pay'}
                            &nbsp;<SVG src='/media/svg/nobilis/vector1.svg' color={formik.isValid ? '' : '#b4b4b4'} />
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    )
}

export default RequestIntroductionPage
